import type { AttachmentInfo } from '@api-client/api'
import { useDownloadAttachment } from '@hooks/useDownloadAttachment'
import { useLocalState } from '@hooks/useLocalState'
import {
	Box,
	Button,
	type ButtonBaseActions,
	CircularProgress,
	FormHelperText,
	FormLabel,
	Link as MuiLink,
	Stack,
} from '@mui/material'
import { SelectFileModal } from '@views/SelectFileModal'
import {
	type ForwardedRef,
	forwardRef,
	useCallback,
	useImperativeHandle,
	useRef,
	useState,
} from 'react'
import { type FieldError, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const SelectedFileItem = ({
	file,
	onRemove,
	onOpen,
	isLoading,
	t,
}: {
	file: AttachmentInfo
	onRemove: (file: AttachmentInfo) => void
	onOpen: (attachmentId: string) => void
	isLoading: boolean
	t: ReturnType<typeof useTranslation>['t']
}) => {
	const handleRemove = useCallback(() => onRemove(file), [file, onRemove])
	const handleOpen = useCallback(() => {
		if (file.id) {
			onOpen(file.id)
		}
	}, [file.id, onOpen])

	return (
		<Box display="flex" alignItems="center">
			<Box mr={2} display="flex" alignItems="center" gap={1}>
				<MuiLink
					component="button"
					onClick={handleOpen}
					disabled={isLoading || !file.id}
					underline="hover"
				>
					{file.name}
				</MuiLink>
				{isLoading && <CircularProgress size={12} />}
			</Box>
			<Button variant="text" color="primary" onClick={handleRemove}>
				{t('button.remove')}
			</Button>
		</Box>
	)
}

type AttachmentLike = Partial<AttachmentInfo>

const mapAttachmentLikeToInfo = (
	attachments: readonly AttachmentLike[]
): readonly AttachmentInfo[] =>
	attachments.map(attachment => ({
		id: attachment.id ?? '',
		name: attachment.name ?? '',
		createdAt: attachment.createdAt ?? '',
		isUsed: attachment.isUsed ?? false,
	}))

interface SelectFileProps {
	fieldError?: FieldError
	formFieldName?:
		| 'verificationData.verificationAttachments'
		| 'emissionsAttachments'
		| 'reportAttachments'
		| 'thirdPartyAuditAttachments'
		| 'additionalDocumentAttachments'
	isRequired?: boolean
	label?: string
	showLabel?: boolean
	templateUrl?: string
	// This should be set to true if the form auto-saves when the form state is 'dirty'
	// If false, the form state will not be marked as dirty, so will not trigger the auto-save
	shouldDirtyOnChange?: boolean
	name?: string
	value?: readonly AttachmentLike[]
	onChange?: (value: unknown) => void
	onAfterChange?: (value: unknown) => void
}

const SelectFile = (
	{
		fieldError,
		formFieldName,
		isRequired,
		label,
		showLabel = false,
		shouldDirtyOnChange = true,
		name,
		templateUrl,
		value,
		onChange,
		onAfterChange,
	}: SelectFileProps,
	ref: ForwardedRef<HTMLButtonElement>
) => {
	const { setValue } = useFormContext()
	const { t } = useTranslation()
	const { isLoading, openAttachmentInNewTab } = useDownloadAttachment()

	const targetFieldName = name ?? formFieldName
	const fallbackFieldName =
		formFieldName && formFieldName !== targetFieldName
			? formFieldName
			: undefined
	const [showModal, setShowModal] = useState<boolean>(false)
	const [selectedFiles, setSelectedFiles] = useLocalState(value, value =>
		Array.isArray(value) ? mapAttachmentLikeToInfo(value) : []
	)

	const selectFileButtonRef = useRef<HTMLButtonElement>(null)
	const selectFileButtonActions = useRef<ButtonBaseActions>(null)
	useImperativeHandle(
		ref,
		() => ({
			...(selectFileButtonRef.current as HTMLButtonElement),
			focus: () => {
				selectFileButtonRef.current?.focus()
				selectFileButtonActions.current?.focusVisible()
				selectFileButtonRef.current?.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
				})
			},
		}),
		[]
	)

	const onSelectFile = useCallback(() => {
		setShowModal(true)
	}, [])

	const setAndUpdateAttachments = useCallback(
		(files: readonly AttachmentInfo[]) => {
			const dataFiles = files.map((file: AttachmentInfo) => ({
				id: file.id,
				name: file.name,
			}))

			if (targetFieldName) {
				setValue(targetFieldName, dataFiles, {
					shouldDirty: shouldDirtyOnChange,
				})
			}

			if (fallbackFieldName) {
				setValue(fallbackFieldName, dataFiles, {
					shouldDirty: shouldDirtyOnChange,
				})
			}

			if (onChange) {
				onChange(dataFiles)
			}

			// Only update the component's state after the setValue so it won't get updated if the step above fails.
			setSelectedFiles(files)

			if (onAfterChange) {
				onAfterChange(dataFiles)
			}
		},
		[
			targetFieldName,
			setValue,
			shouldDirtyOnChange,
			fallbackFieldName,
			onChange,
			onAfterChange,
			setSelectedFiles,
		]
	)

	const onRemove = useCallback(
		(file: AttachmentInfo) => {
			const selectedIndex = selectedFiles.findIndex(
				(obj: AttachmentInfo) => obj.id === file.id
			)
			const newSelected = [
				...selectedFiles.slice(0, selectedIndex),
				...selectedFiles.slice(selectedIndex + 1),
			]
			setAndUpdateAttachments(newSelected)
		},
		[selectedFiles, setAndUpdateAttachments]
	)

	const showSelectedFiles =
		selectedFiles && selectedFiles.length > 0 && !showModal

	return (
		<Box>
			<SelectFileModal
				isModalOpened={showModal}
				selectedFiles={selectedFiles}
				onConfirm={setAndUpdateAttachments}
				onModalClose={setShowModal}
			/>
			<Stack gap={0.5} alignItems="flex-start">
				{showLabel && label && (
					<FormLabel required={isRequired}>{label}</FormLabel>
				)}
				<Box>
					{showSelectedFiles &&
						selectedFiles.map((file, key) => (
							<SelectedFileItem
								key={key}
								file={file}
								onRemove={onRemove}
								onOpen={openAttachmentInNewTab}
								isLoading={isLoading}
								t={t}
							/>
						))}
				</Box>
				<Stack flexDirection="row" gap={1}>
					<Button
						variant="outlined"
						color="primary"
						onClick={onSelectFile}
						action={selectFileButtonActions}
						ref={selectFileButtonRef}
					>
						{t('button.selectFile')}
					</Button>
					{templateUrl && (
						<Button
							variant="text"
							color="primary"
							component="a"
							href={templateUrl}
							download
						>
							{t('button.downloadTemplate')}
						</Button>
					)}
				</Stack>
				{fieldError && !showSelectedFiles && (
					<Box height={6}>
						<FormHelperText error>
							{fieldError.message || t('input.required')}
						</FormHelperText>
					</Box>
				)}
			</Stack>
		</Box>
	)
}

export default forwardRef(SelectFile)
